import { useCallback } from "react";
import { array, bool, func } from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

import { colors, fonts } from "../../../styles/Styles.variables";
import { contacts } from "../../../constants/contacts";
import { routes } from "../../../constants/routes";
import { RESTRICT_LISTINGS } from "../../../constants/url";

import { Link } from "react-router-dom";

import LocationsList from "../../locations-list";

const listingsLinks = routes.results.categories;

function PageDesktopFooterNav({
  authenticated = false,
  settingsLinks,
  trackOutboundLink,
}) {
  return (
    <FooterNav authenticated={authenticated}>
      {(RESTRICT_LISTINGS && authenticated) ||
        (!RESTRICT_LISTINGS && <PrimaryNav />)}
      {authenticated && <AdminNav settingsLinks={settingsLinks} />}
      {!authenticated && <SecondaryNav />}
      {!authenticated && <SocialNav trackOutboundLink={trackOutboundLink} />}
    </FooterNav>
  );
}

function PrimaryNav() {
  return (
    <FooterNavColumn>
      <FooterNavHeading>Listings</FooterNavHeading>
      <FooterNavList>
        {listingsLinks.map(({ path, title }) => (
          <li key={path}>
            <Link to={path}>{title}</Link>
          </li>
        ))}
      </FooterNavList>
    </FooterNavColumn>
  );
}

function AdminNav({ settingsLinks }) {
  return (
    <FooterNavColumn>
      <FooterNavHeading>Tools</FooterNavHeading>
      <FooterNavList>
        <li>
          <Link to={routes.compile.path}>{routes.compile.title}</Link>
        </li>
        <li>
          <Link to={routes.blog.path}>{routes.blog.title}</Link>
        </li>
      </FooterNavList>
      <FooterNavHeading>My&nbsp;Settings</FooterNavHeading>
      <FooterNavList>
        {settingsLinks.map(({ path, title }) => (
          <li key={path}>
            <Link to={path}>{title}</Link>
          </li>
        ))}
      </FooterNavList>
    </FooterNavColumn>
  );
}

function SecondaryNav() {
  return (
    <FooterNavColumn>
      <FooterNavHeading>Company</FooterNavHeading>
      <FooterNavList>
        <li>
          <Link to={routes.partners.path}>{routes.partners.title}</Link>
        </li>
        <li>
          <Link to={routes.why.path}>{routes.why.title}</Link>
        </li>
        <li>
          <Link to={routes.contact.path}>Contact</Link>
        </li>
        <li>
          <Link to={routes.consignment.path}>Consign</Link>
        </li>
      </FooterNavList>
      <FooterNavHeading>Locations</FooterNavHeading>
      <FooterNavList as={LocationsList} />
    </FooterNavColumn>
  );
}

function SocialNav({ trackOutboundLink }) {
  const trackClick = useCallback(
    (e, action, label = "footer") => {
      if (!!trackOutboundLink) {
        trackOutboundLink(e, { action, label });
      }
    },
    [trackOutboundLink]
  );

  return (
    <FooterNavColumn>
      <FooterNavHeading>Follow</FooterNavHeading>
      <FooterNavList>
        <li>
          <Link to={routes.blog.path}>{routes.blog.title}</Link>
        </li>
        <li>
          <a
            href={contacts.social.facebook}
            target={"_blank"}
            rel="noreferrer"
            onClick={e => {
              trackClick(e, "facebook");
            }}
          >
            Facebook
          </a>
        </li>
        <li>
          <a
            href={contacts.social.linkedIn}
            target={"_blank"}
            rel="noreferrer"
            onClick={e => {
              trackClick(e, "linkedIn");
            }}
          >
            LinkedIn
          </a>
        </li>
        <li>
          <a
            href={contacts.social.instagram}
            target={"_blank"}
            rel="noreferrer"
            onClick={e => {
              trackClick(e, "instagram");
            }}
          >
            Instagram
          </a>
        </li>
        <li>
          <a
            href={contacts.social.twitter}
            target={"_blank"}
            rel="noreferrer"
            onClick={e => {
              trackClick(e, "twitter");
            }}
          >
            Twitter
          </a>
        </li>
        <li>
          <Link
            onClick={e => {
              trackClick(e, "Weekly Used Equipment Email Sign-Up Link");
            }}
            to={routes.emailSignup.path}
          >
            Weekly Updates
          </Link>
        </li>
      </FooterNavList>
    </FooterNavColumn>
  );
}

const FooterNav = styled.div`
  display: flex;
  flex: 1 1;
  flex-wrap: wrap;
  justify-content: flex-start;
  min-width: ${rem("250px")};
  max-width: ${({ authenticated }) =>
    authenticated ? rem("240px") : rem("430px")};

  @media (min-width: ${rem("1240px")}) {
    flex-wrap: nowrap;
    justify-content: space-between;
    max-width: ${({ authenticated }) =>
      authenticated ? rem("312px") : rem("502px")};
  }
`;

const FooterNavColumn = styled.div`
  padding: 0 ${rem("18px")};
`;

const FooterNavHeading = styled.h2`
  color: ${colors.white};
  font-size: ${rem("12px")};
  font-weight: ${fonts.weights.bold};
  line-height: 1;
  margin-bottom: ${rem("12px")};
  text-transform: uppercase;

  @media (min-width: ${rem("1240px")}) {
    font-size: ${rem("15px")};
  }
`;

const FooterNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 ${rem("14px")};

  li {
    display: block;
    margin-bottom: ${rem("11px")};
  }

  a {
    color: ${colors.gray300};
    display: block;
    font-size: ${rem("12px")};
    outline: none;
    text-decoration: none;
    transition: color 0.3s ease-in-out;
    white-space: nowrap;

    @media (min-width: ${rem("1240px")}) {
      font-size: ${rem("15px")};
    }

    &:active,
    &:focus,
    &:hover {
      color: ${colors.white};
    }
  }
`;

PageDesktopFooterNav.propTypes = {
  authenticated: bool,
  settingsLinks: array,
  trackOutboundLink: func.isRequired,
};

export default PageDesktopFooterNav;
